import { useEffect, useMemo, useState } from 'react';
import { supabase, isMissingFunction } from '../supabase';
import { useCopy } from '../i18n/LanguageContext';
import { IS_NATIVE } from '../config/platform';

/**
 * QuizAanmelding — Supabase-gedreven aanmeldformulier voor "Test jezelf".
 *
 * Upgrade op de vrije-tekst context-stap in Quiz.jsx: in plaats van
 * organisatie/afdeling/teamcode zelf te typen (typo-gevoelig, terwijl de
 * team-matching exact op invite_code draait), kiest de deelnemer hier zijn
 * team uit een dropdown.
 *
 * Bron = Supabase-functie `quiz_teams` (008): organisatie + team, ZONDER
 * codes. Vroeger las dit formulier de hele tabel `team_access_codes`, en
 * daarmee kon iedereen alle teamcodes opvragen. De code komt er nu op de
 * server bij: een trigger op private.responses zoekt hem op bij organisatie
 * + afdeling. Alleen de code uit de uitnodigingslink (?code=) sturen we zelf
 * mee — die heeft de bezoeker al.
 *
 * Props:
 *   - organisatie : optioneel. Vast-zetten op één organisatie. Leeg = de
 *                   deelnemer kiest zelf uit alle organisaties met codes.
 *   - onSubmit(profile) : ontvangt het profiel in saveResponse-vorm:
 *                   { name, org, dept, team, invite_code }
 */
export default function QuizAanmelding({ organisatie = '', onSubmit }) {
  const { signup: t } = useCopy();

  // URL-parameters uit de uitnodigingsmail: ?org=...&code=...
  // Hiermee weet de tool meteen om welke organisatie + team het gaat.
  const urlParams = useMemo(() => {
    if (typeof window === 'undefined') return { org: '', code: '' };
    const p = new URLSearchParams(window.location.search);
    return {
      org: String(p.get('org') || '').trim(),
      code: String(p.get('code') || '').trim(),
    };
  }, []);

  const orgVast = String(organisatie || '').trim() || urlParams.org;

  // Komt de bezoeker via een uitnodigingslink? Manager-links bevatten altijd
  // ?org= én ?code=. Zonder link mag de bezoeker alleen demo-organisaties zien;
  // echte organisaties (Gemeente Nijkerk, The Office Factory, …) blijven dan
  // afgeschermd.
  const viaLink = Boolean(urlParams.org || urlParams.code);
  const isDemoOrg = (naam) => String(naam || '').toLowerCase().includes('demo');

  const [rijen, setRijen] = useState([]); // [{ organization, team, code, linkTeam }]
  const [laden, setLaden] = useState(!IS_NATIVE);
  const [fout, setFout] = useState('');

  const [form, setForm] = useState({
    organisatie: orgVast,
    afdeling: '',
    teamcode: '',
    voornaam: '',
    team: '',
    overJezelf: '',
  });

  // Actieve team-codes ophalen uit Supabase.
  // In app-modus bestaan teams niet: alles blijft op het toestel, dus er
  // wordt hier niets opgehaald.
  useEffect(() => {
    if (IS_NATIVE) return undefined;
    let actief = true;
    async function haalCodes() {
      setLaden(true);
      setFout('');

      if (!supabase) {
        if (actief) {
          setFout(t.errors.noConnection);
          setRijen([]);
          setLaden(false);
        }
        return;
      }

      let { data, error } = await supabase.rpc('quiz_teams', {
        p_org: orgVast || null,
        p_code: urlParams.code || null,
      });

      // Terugval zolang 008 nog niet gedraaid is: de oude, directe read.
      if (isMissingFunction(error)) {
        ({ data, error } = await supabase
          .from('team_access_codes')
          .select('organization, team, code')
          .eq('active', true)
          .order('organization', { ascending: true })
          .order('team', { ascending: true }));
      }
      if (!actief) return;

      if (error) {
        setFout(t.errors.loadFailed);
        setRijen([]);
      } else {
        setRijen(
          (data || [])
            .map((r) => {
              const code = String(r.code || '').trim();
              return {
                organization: String(r.organization || '').trim(),
                team: String(r.team || '').trim(),
                code,
                linkTeam: Boolean(r.is_link_team) || (Boolean(code) && code === urlParams.code),
              };
            })
            .filter((r) => r.team)
        );
      }
      setLaden(false);
    }
    haalCodes();
    return () => {
      actief = false;
    };
  }, [t, orgVast, urlParams.code]);

  // De code die met een afdeling meegaat: alleen de code uit de link, en dan
  // alleen voor het team van die link. Voor de rest vult de server hem in.
  // (In de terugval van vóór 008 kent de rij zijn code nog zelf.)
  const codeVoor = (rij) => (rij ? rij.code || (rij.linkTeam ? urlParams.code : '') : '');

  // Teamcode uit de URL: zodra de codes geladen zijn de bijbehorende
  // organisatie + afdeling automatisch voorselecteren.
  useEffect(() => {
    if (!urlParams.code || form.afdeling) return;
    const match = rijen.find((r) => r.linkTeam);
    if (match) {
      setForm((f) => ({
        ...f,
        organisatie: match.organization,
        afdeling: match.team,
        teamcode: urlParams.code,
      }));
    }
  }, [rijen, urlParams.code, form.afdeling]);

  // Zichtbare rijen: zonder uitnodigingslink alleen demo-organisaties tonen,
  // zodat een vrije bezoeker geen echte organisaties kan zien of kiezen.
  const zichtbareRijen = useMemo(
    () => (viaLink ? rijen : rijen.filter((r) => isDemoOrg(r.organization))),
    [rijen, viaLink]
  );

  // Unieke organisaties (alleen relevant als er geen vaste organisatie is).
  const organisaties = useMemo(() => {
    const set = new Set(zichtbareRijen.map((r) => r.organization).filter(Boolean));
    return [...set].sort((a, b) => a.localeCompare(b, 'nl'));
  }, [zichtbareRijen]);

  // Afdelingen binnen de gekozen organisatie.
  const afdelingen = useMemo(() => {
    const org = form.organisatie;
    if (!org) return [];
    return zichtbareRijen.filter((r) => r.organization === org);
  }, [zichtbareRijen, form.organisatie]);

  // Demo-modus (zonder link): is er maar één zichtbare organisatie, dan valt
  // er niets te kiezen — meteen invullen in plaats van een dropdown met één
  // optie tonen.
  const organisatieVast = !viaLink && !orgVast && organisaties.length === 1;

  useEffect(() => {
    if (!organisatieVast || form.organisatie) return;
    setForm((f) => ({ ...f, organisatie: organisaties[0] }));
  }, [organisatieVast, organisaties, form.organisatie]);

  // Demo-modus (zonder link): bij één afdeling geen keuze bieden, maar de
  // afdeling automatisch invullen.
  const afdelingVast = !viaLink && afdelingen.length === 1;

  useEffect(() => {
    if (!afdelingVast || form.afdeling) return;
    const a = afdelingen[0];
    setForm((f) => ({ ...f, afdeling: a.team, teamcode: codeVoor(a) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [afdelingVast, afdelingen, form.afdeling]);

  // Solo-modus: iemand test de tool voor zichzelf, zonder uitnodigingslink.
  // Organisatie, afdeling en teamcode staan dan vast — dat is administratie,
  // geen keuze. Die velden verbergen we; alleen "wie ben jij" blijft over.
  // In app-modus is er per definitie niemand anders: geen organisatie, geen
  // afdeling, geen teamcode — alleen "wie ben jij".
  //
  // Dit leidt bewust af uit de data en niet uit `organisatieVast && afdelingVast`:
  // die tweede hangt aan `form.organisatie`, die pas in een effect wordt gezet.
  // In de render ertussen stond solo-modus even op false en flitste "Kies je
  // team" voorbij. Eén zichtbare rij = één organisatie met één afdeling, en dat
  // weten we meteen.
  const soloModus = IS_NATIVE || (!viaLink && !orgVast && zichtbareRijen.length === 1);

  function kiesOrganisatie(e) {
    const org = e.target.value;
    setForm((f) => ({ ...f, organisatie: org, afdeling: '', teamcode: '' }));
  }

  // Bij keuze afdeling: de code uit de link onthouden als het dat team is.
  function kiesAfdeling(e) {
    const naam = e.target.value;
    const gekozen = afdelingen.find((a) => a.team === naam);
    setForm((f) => ({
      ...f,
      afdeling: naam,
      teamcode: codeVoor(gekozen),
    }));
  }

  function wijzig(veld) {
    return (e) => setForm((f) => ({ ...f, [veld]: e.target.value }));
  }

  function verstuur(e) {
    e.preventDefault();
    if (!IS_NATIVE) {
      if (!form.organisatie) {
        setFout(t.errors.pickOrganization);
        return;
      }
      if (!form.afdeling) {
        setFout(t.errors.pickDepartment);
        return;
      }
    }
    setFout('');

    const profile = {
      name: form.voornaam.trim(),
      org: form.organisatie,
      dept: form.afdeling,
      // Team is optioneel; valt terug op de afdeling als leeg.
      team: form.team.trim() || form.afdeling,
      // Leeg = de server zoekt de code bij organisatie + afdeling (008).
      invite_code: form.teamcode,
      role: form.overJezelf.trim(),
    };

    if (typeof onSubmit === 'function') {
      onSubmit(profile);
    } else {
      // Geen handler aangesloten — log voor ontwikkeldoeleinden.
      console.log('Aanmelding:', profile);
    }
  }

  // ── Styling — inline, in lijn met Quiz.jsx / Intro (TOF-palet) ──────────
  const S = {
    page: {
      minHeight: 'calc(100vh - 88px)',
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'center',
      padding: '24px 20px 40px',
      background: '#f7f2ec',
      boxSizing: 'border-box',
    },
    card: {
      width: '100%',
      maxWidth: 560,
      background: 'white',
      borderRadius: 20,
      borderTop: '4px solid #b85c5c',
      border: '1px solid #ddd6ce',
      boxShadow: '0 12px 32px rgba(31,27,24,0.08)',
      padding: '28px 28px 32px',
    },
    eyebrow: {
      fontSize: 12,
      textTransform: 'uppercase',
      letterSpacing: 1.5,
      fontWeight: 600,
      color: '#b85c5c',
      marginBottom: 6,
    },
    title: {
      fontFamily: 'Playfair Display',
      fontSize: 26,
      lineHeight: 1.2,
      margin: '0 0 18px 0',
      color: '#1f1b18',
      fontWeight: 500,
    },
    label: {
      display: 'block',
      fontWeight: 600,
      marginTop: 16,
      marginBottom: 6,
      color: '#3f3833',
      fontSize: 14,
    },
    optional: { fontWeight: 400, color: '#a89e96', fontSize: 13 },
    required: { color: '#b85c5c' },
    input: {
      width: '100%',
      padding: '11px 14px',
      border: '1px solid #ddd',
      borderRadius: 12,
      fontSize: 15,
      color: '#1f1b18',
      background: 'white',
      boxSizing: 'border-box',
      outline: 'none',
    },
    readonly: {
      width: '100%',
      padding: '11px 14px',
      border: '1px solid #e6dfd8',
      borderRadius: 12,
      fontSize: 15,
      background: '#f4efe9',
      color: '#7a6d66',
      boxSizing: 'border-box',
    },
    hint: { fontSize: 13, color: '#8a7f78', marginTop: 6 },
    error: { fontSize: 14, color: '#b85c5c', marginTop: 12 },
    submit: {
      marginTop: 24,
      width: '100%',
      padding: '13px 22px',
      background: '#1f1b18',
      color: 'white',
      fontSize: 15,
      fontWeight: 600,
      border: 'none',
      borderRadius: 12,
      cursor: 'pointer',
      boxShadow: '0 4px 14px rgba(31,27,24,0.18)',
    },
  };

  // Zolang de codes laden is `rijen` leeg, en dan lijkt elke bezoeker een
  // teamkiezer: `soloModus` kan pas kloppen als we weten hoeveel organisaties
  // er zijn. Tot die tijd tonen we geen kop en geen velden — anders flitst
  // "Kies je team" voorbij bij iemand die in zijn eentje test.
  if (laden) {
    return (
      <div className="fade-up" style={S.page}>
        <div style={S.card}>
          <div style={S.eyebrow}>{t.eyebrow}</div>
          <p style={{ ...S.hint, marginTop: 0 }}>{t.organizationLoading}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fade-up" style={S.page}>
      <form onSubmit={verstuur} style={S.card}>
        <div style={S.eyebrow}>{t.eyebrow}</div>
        <h1 style={S.title}>{soloModus ? t.soloTitle : t.title}</h1>

        {soloModus ? (
          <p style={{ ...S.hint, marginTop: 0 }}>{t.soloLead}</p>
        ) : (
          <>
            {/* Organisatie — dropdown, met voorgeselecteerde waarde via ?org= parameter */}
            <label style={S.label}>
              {t.organizationLabel} <span style={S.required}>*</span>
            </label>
            {orgVast ? (
              <input style={S.readonly} value={orgVast} readOnly />
            ) : (
              <select
                style={S.input}
                value={form.organisatie}
                onChange={kiesOrganisatie}
                required
                disabled={laden}
              >
                <option value="" disabled>
                  {laden ? t.organizationLoading : t.organizationPlaceholder}
                </option>
                {organisaties.map((org) => (
                  <option key={org} value={org}>
                    {org}
                  </option>
                ))}
              </select>
            )}

            {/* Afdeling — dropdown uit Supabase, of vast (readonly) in demo-modus */}
            <label style={S.label}>
              {t.departmentLabel} <span style={S.required}>*</span>
            </label>
            {afdelingVast ? (
              <input style={S.readonly} value={form.afdeling} readOnly />
            ) : (
              <select
                style={S.input}
                value={form.afdeling}
                onChange={kiesAfdeling}
                required
                disabled={laden || !form.organisatie}
              >
                <option value="" disabled>
                  {laden
                    ? t.departmentLoading
                    : !form.organisatie
                    ? t.departmentNeedsOrganization
                    : afdelingen.length === 0
                    ? t.departmentEmpty
                    : t.departmentPlaceholder}
                </option>
                {afdelingen.map((a) => (
                  <option key={a.team} value={a.team}>
                    {a.team}
                  </option>
                ))}
              </select>
            )}

          </>
        )}

        {/* Optionele velden */}
        <label style={S.label}>
          {t.firstNameLabel} <span style={S.optional}>{t.optional}</span>
        </label>
        <input
          style={S.input}
          value={form.voornaam}
          onChange={wijzig('voornaam')}
          placeholder={t.firstNamePlaceholder}
        />
        <p style={S.hint}>{IS_NATIVE ? t.firstNameHintNative : t.firstNameHint}</p>

        {!soloModus && (
          <>
            <label style={S.label}>
              {t.teamLabel} <span style={S.optional}>{t.optional}</span>
            </label>
            <input
              style={S.input}
              value={form.team}
              onChange={wijzig('team')}
              placeholder={t.teamPlaceholder}
            />
          </>
        )}

        <label style={S.label}>
          {t.aboutLabel} <span style={S.optional}>{t.optional}</span>
        </label>
        <textarea
          style={{ ...S.input, minHeight: 96, resize: 'vertical', fontFamily: 'inherit' }}
          value={form.overJezelf}
          onChange={wijzig('overJezelf')}
        />

        {fout && <p style={S.error}>{fout}</p>}

        <button type="submit" style={S.submit}>
          {t.submit}
        </button>
      </form>
    </div>
  );
}
